import videojs, { VideoJsPlayer } from 'video.js';

import { AdsManagerPluginFactoryFn } from '@src/plugins/AdsManagerPlugin';
import { ContextPluginFactoryFn } from '@src/plugins/ContextPlugin';
import { ControllerFactoryFn } from '@src/plugins/Controller';
import { EventListenerPluginFactoryFn } from '@src/plugins/EventListenerPlugin';
import { EventManagerPluginFactoryFn } from '@src/plugins/EventManagerPlugin';
import type { QualityLevelsManagerPluginFactoryFn } from '@src/plugins/QualityLevelsManagerPlugin';
import { UIManagerPluginFactoryFn } from '@src/plugins/UIManagerPlugin';
import { Theme } from '@src/types/theme';

export const Player = videojs.getComponent('Player');

const ENABLE_PATCH_FIREFOX_PLAY_PENDING_PROMISE = true;

const patchFirefoxPlayPendingPromise = (vjsPlayer: VideoJsPlayer) => {
  // firefox desktop/mobile can never guarantee resolve the promise from player.play()
  // which cause autoplay mostly fail, so set timeout to reject the 1st play promise and retry again
  if (videojs.browser.IS_FIREFOX) {
    const originalPlay = vjsPlayer.play;
    vjsPlayer.play = async function () {
      let retryCount = 0;

      const autoplayConfig = vjsPlayer.autoplay();
      const isProgrammaticAutoplay = ['any', 'play', 'muted'].includes(
        autoplayConfig as string
      );

      const duration = vjsPlayer.duration();
      const currentTime = vjsPlayer.currentTime();

      const isHandleTimeout =
        isProgrammaticAutoplay &&
        (vjsPlayer.hasStarted() === false || Math.floor(currentTime) === 0) &&
        duration !== Infinity;

      let isHandleRestoreMuted = true;

      const playPromise = await Promise.race<void>([
        originalPlay.bind(vjsPlayer)(),
        ...(isHandleTimeout
          ? [
              new Promise<void>((_, reject) => {
                setTimeout(() => reject('E_TIMEOUT'), 1000);
              }),
            ]
          : []),
      ]).catch((err) => {
        if (err === 'E_TIMEOUT') {
          isHandleRestoreMuted = false;
        }

        if (isHandleTimeout && err === 'E_TIMEOUT' && retryCount < 1) {
          retryCount += 1;
          return originalPlay.bind(vjsPlayer)();
        }

        throw err;
      });

      if (
        isHandleRestoreMuted &&
        !vjsPlayer.contextPlugin.state.userVolumeChangeCount &&
        !vjsPlayer.contextPlugin.state.adVolumeChangeCount &&
        !vjsPlayer.contextPlugin.state.clickToPlayCount &&
        vjsPlayer.autoplay() === 'muted' &&
        !vjsPlayer.muted()
      ) {
        vjsPlayer.muted(true);
      }

      return playPromise;
    }.bind(vjsPlayer);
  }
};

videojs.hook('setup', (vjsPlayer: VideoJsPlayer) => {
  const { options_ } = vjsPlayer;

  if (videojs.browser.IS_ANDROID) {
    vjsPlayer.addClass('vjs-android');
  }

  if (!vjsPlayer.usingPlugin('contextPlugin')) {
    (vjsPlayer.contextPlugin as unknown as ContextPluginFactoryFn)?.({
      initialBlockAd: options_.blockAd,
    });
  }

  if (!vjsPlayer.usingPlugin('eventListenerPlugin')) {
    (
      vjsPlayer.eventListenerPlugin as unknown as EventListenerPluginFactoryFn
    )?.();
  }

  if (!vjsPlayer.usingPlugin('eventManagerPlugin')) {
    (
      vjsPlayer.eventManagerPlugin as unknown as EventManagerPluginFactoryFn
    )?.();
  }

  if (!vjsPlayer.usingPlugin('qualityLevelsManagerPlugin')) {
    (
      vjsPlayer.qualityLevelsManagerPlugin as unknown as QualityLevelsManagerPluginFactoryFn
    )?.();
  }

  if (!vjsPlayer.usingPlugin('adsManagerPlugin')) {
    (vjsPlayer.adsManagerPlugin as unknown as AdsManagerPluginFactoryFn)?.();
  }

  if (!vjsPlayer.usingPlugin('controller')) {
    (vjsPlayer.controller as unknown as ControllerFactoryFn)?.();
  }

  if (options_.theme === Theme.V5DEFAULT) {
    if (!vjsPlayer.usingPlugin('uiManagerPlugin')) {
      (vjsPlayer.uiManagerPlugin as unknown as UIManagerPluginFactoryFn)?.();
    }
  }

  if (options_.focusOnInit) {
    vjsPlayer.focus();
  }

  if (ENABLE_PATCH_FIREFOX_PLAY_PENDING_PROMISE) {
    patchFirefoxPlayPendingPromise(vjsPlayer);
  }
});
